export const q = document.querySelector.bind(document);
export const qa = document.querySelectorAll.bind(document);

export const State = (obj) => {
    if (!obj)
        return null;

    let out = { ___state: {} };
    let subscribers = {};
    let bindings = {};

    let shortInputBinds = [{
        types: ['text', 'number', 'phone', 'email'],
        events: ['keyup', 'change'],
        listener: (o, prop) => e => {
            o[prop] = e.target.value;
        },
        setter: (el, v) => {
            el.value = v;
        }
    }, {
        types: ['checkbox'],
        events: ['change'],
        listener: (o, prop) => e => {
            o[prop] = e.target.checked;
        },
        setter: (el, v) => {
            el.checked = v;
        }
    }, {
        types: ['select-one'],
        events: ['change'],
        listener: (o, prop) => e => {
            o[prop] = e.target.value;
        },
        setter: (el, v) => {
            el.value = v;
        }
    }];

    let stateChangeSubscribers = [];
    let changedProps = [];
    let stateChangeTimer = null;
    let render = (prop) => {
        if (stateChangeSubscribers.length === 0)
            return;

        changedProps.push(prop);
        if (stateChangeTimer)
            clearTimeout(stateChangeTimer);

        stateChangeTimer = setTimeout(() => {
            if (process.env.NODE_ENV === 'development') {
                console.info(`⚙ changed: props ${[...new Set(changedProps)].join(',')} changed`);
                // console.trace();
            }

            for (const renderer of stateChangeSubscribers)
                renderer({ ...out.___state }, [...new Set(changedProps)]); //to avoid mutations
            changedProps = [];
        }, 10);
    };

    const addProps = (x) => {
        for (const prop of Object.keys(x)) {
            if (!Object.prototype.hasOwnProperty.call(x, prop))
                continue;

            if (!subscribers[prop])
                subscribers[prop] = [];
            if (!bindings[prop])
                bindings[prop] = [];

            if (typeof x[prop] === 'function' || prop === '___state')
                continue;

            if (out.___state[prop] !== undefined) {
                out.___state[prop] = x[prop];
                continue;
            }
            out.___state[prop] = x[prop];

            Object.defineProperty(out, prop, {
                get: () => {
                    return out.___state[prop];
                },
                set: v => {
                    if (out.___state[prop] === v) //value did not change
                        return;

                    for (const fn of subscribers[prop])
                        fn(v, out[prop]);

                    for (const binding of bindings[prop]) {
                        const { el, setter } = binding;
                        setter(el, v);
                    }

                    if (process.env.NODE_ENV === 'development') {
                        console.info(`⚙ prop ${prop}:`, out[prop], '->', v);
                        // console.trace();
                    }
                    out.___state[prop] = v;
                    render(prop);
                }
            });
        }
    };
    addProps(obj);

    out.extend = o => {
        if (!o)
            return;

        if (o.___state)
            throw new Error('can\'t extend with another State object');

        addProps(o);
    };

    out.changed = fn => { stateChangeSubscribers.push(fn); };

    out.bind = (prop, element) => {
        if (!element)
            throw ('A HTMLElement or function is required');
        if (out[prop] === undefined)
            throw new Error(`bind: property ${prop} does not exist`);

        if (typeof (element) === 'function') {
            subscribers[prop].push(element);
            element(out[prop], out[prop]); //initial value
            return;
        }

        //bind to HTMLElement
        if (!(element instanceof HTMLElement))
            throw new Error('bind: element is not an instance of HTMLElement', element);

        let found = false;
        for (const bind of shortInputBinds) {
            for (const type of bind.types) {
                if (element.type === type) {
                    found = true;
                    if (bind.events && bind.listener) {
                        for (const event of bind.events)
                            element.addEventListener(event, bind.listener(out, prop));
                    }
                    bindings[prop].push({ el: element, setter: bind.setter, event: bind.event });

                    //in in case of autofill from browsers
                    requestAnimationFrame(() => { bind.setter(element, out[prop]); }); //initial value
                    break;
                }
            }
        }
        if (!found)
            throw new Error(`bind: element of type ${element.type} is not supported`, element);
    };

    out.unbind = () => {
        for (const key of Object.keys(subscribers))
            delete subscribers[key];
        for (const key of Object.keys(bindings)) {
            for (const binding of bindings[key]) {
                binding.el.removeEventListener(binding.event, binding.setter);
            }
            delete bindings[key];
        }
        stateChangeSubscribers = [];
    };

    return out;
};

export const AttachState = (initialState, binder) => {
    return (root, refs, overrideState) => {
        let state = State({});
        if (root.root) {//component instead of HTMLElement
            if (root.state) {
                root.state.extend(initialState);
            }

            state = root.state;
            root = root.root;
        }
        else {
            state.extend(initialState);
        }

        if (overrideState)
            state = overrideState;

        if (!(root instanceof HTMLElement))
            throw new Error('instance requires an HTMLElement');

        binder(state, root, refs);
        let component = {
            state,
            root,
            remove: () => {
                component.state.unbind();
                delete component.state;
                delete component.root;
                delete component.remove;
                //event listeners will be garbage collected
                root.remove();
            }
        };

        return component;
    };
};

export const getField = (obj, field) => {
    if (obj
        && obj[field]
        && obj[field].toString().length > 0
        && obj[field].toString() != '[object Object]')
        return obj[field].toString();

    return null;
};